import { formatCompact } from "@/utils/tokens/chartConfig";
import { MetricKeys } from "@/constants/metrics";
import type { TokenRow } from "@/types/movers";

export function formatPct(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  const s = n >= 0 ? '+' : '';
  return `${s}${n.toFixed(Math.abs(n) >= 100 ? 0 : 2)}%`;
}

export function getCellNumber(r: TokenRow, key: string): number | null {
  const v = (r[key] as number | null) ?? null;
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function formatFundingAPR(rawRatePercent: number): string {
  // rawRatePercent already in percent units; convert to daily 8h periods then annualize
  const apr = rawRatePercent * 3 * 365; // 3 periods per day × 365 days
  const sign = apr >= 0 ? '+' : '';
  return `${sign}${Math.abs(apr).toFixed(2)}%`;
}

export function formatActualValue(key: string, v: number | null | undefined): string {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);

  if (key === MetricKeys.PriceClose) {
    return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })}`;
  }

  if (key === MetricKeys.FuturesOI) {
    return `$${formatCompact(n)}`;
  }

  if (key === MetricKeys.FuturesFR || key === MetricKeys.FuturesFROIW || key === MetricKeys.FuturesFRVolW) {
    const sign = n >= 0 ? '+' : '';
    return `${sign}${Math.abs(n).toFixed(3)}%`;
  }

  if (key === MetricKeys.MarketCap || key === MetricKeys.Volume || key === MetricKeys.Fees || key === MetricKeys.TVL) {
    return `$${formatCompact(n)}`;
  }

  if (key === MetricKeys.DAU || key === MetricKeys.TxCount) {
    return `${formatCompact(n)}`;
  }

  if (key === MetricKeys.Vol30d || key === MetricKeys.Vol90d) {
    return `${n.toFixed(1)}%`;
  }

  if (key === MetricKeys.Sharpe30d) {
    return `${n.toFixed(2)}`;
  }

  return `${formatCompact(n)}`;
}

export const METRIC_LABELS: Record<string, string> = {
  [MetricKeys.PriceClose]: 'Price',
  [MetricKeys.Volume]: 'Volume',
  [MetricKeys.MarketCap]: 'Market Cap',
  [MetricKeys.FuturesOI]: 'Open Interest',
  [MetricKeys.Fees]: 'Fees',
  [MetricKeys.Revenue]: 'Revenue',
  [MetricKeys.TVL]: 'TVL',
  [MetricKeys.DAU]: 'DAU',
  [MetricKeys.TxCount]: 'Transactions',
  [MetricKeys.FuturesFROIW]: 'Funding Rate',
  [MetricKeys.FuturesFRVolW]: 'Funding Rate',
  [MetricKeys.Vol30d]: 'Vol 30d',
  [MetricKeys.Vol90d]: 'Vol 90d',
  [MetricKeys.Sharpe30d]: 'Sharpe 30d',
};

export const PANEL_BASE_CLASS = 'bg-gray-800/30 rounded-xl shadow-lg border-0';

